/**
 * AuditDiffView
 *
 * Renders a jsondiffpatch delta as a compact red/green list. We render our
 * own version rather than using jsondiffpatch's bundled HTML formatter so
 * the styling matches the rest of the admin design system (Cormorant + tan
 * palette + tight typography).
 *
 * jsondiffpatch delta format reference:
 *   - Added field:        "x": [newValue]
 *   - Removed field:      "x": [oldValue, 0, 0]
 *   - Changed field:      "x": [oldValue, newValue]
 *   - Long-text diff:     "x": [textDiff, 0, 2]   (rare; we render as "changed")
 *   - Array container:    { "_t": "a", "0": ..., "_1": [removed, 0, 0] }
 *
 * Nested objects appear as plain objects without the array-shaped wrapper.
 */

import React from 'react';

const fmt = (v) => {
  if (v === null || v === undefined) return <em className="text-[#9a9080]">empty</em>;
  if (typeof v === 'object') return <code className="text-[10.5px]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{JSON.stringify(v)}</code>;
  if (typeof v === 'string' && v.length > 120) return v.slice(0, 120) + '…';
  if (v instanceof Date) return v.toISOString();
  return String(v);
};

function ChangeRow({ pathKey, oldVal, newVal, kind }) {
  // kind: 'added' | 'removed' | 'changed'
  const palette = {
    added:   { bg: '#e8efe5', border: '#d3e2cb', label: 'ADDED',   color: '#4a6b3f' },
    removed: { bg: '#f2e9e6', border: '#e6d2cb', label: 'REMOVED', color: '#9a564a' },
    changed: { bg: '#f3ebd7', border: '#e7d7ad', label: 'CHANGED', color: '#8a6a1f' },
  }[kind];
  return (
    <div className="flex items-start gap-3 px-3 py-2 rounded-[8px] border" style={{ background: palette.bg, borderColor: palette.border }}>
      <span className="text-[8.5px] font-bold uppercase tracking-wider mt-[3px]" style={{ color: palette.color }}>
        {palette.label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold text-[#08111f] truncate" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{pathKey}</div>
        {kind === 'changed' && (
          <div className="text-[11.5px] mt-0.5 leading-snug">
            <span className="line-through text-[#9a564a]/80">{fmt(oldVal)}</span>
            <span className="mx-1.5 text-[#7a7060]">→</span>
            <span className="text-[#4a6b3f]">{fmt(newVal)}</span>
          </div>
        )}
        {kind === 'added' && (
          <div className="text-[11.5px] mt-0.5 leading-snug text-[#4a6b3f]">{fmt(newVal)}</div>
        )}
        {kind === 'removed' && (
          <div className="text-[11.5px] mt-0.5 leading-snug text-[#9a564a] line-through">{fmt(oldVal)}</div>
        )}
      </div>
    </div>
  );
}

function flattenDelta(delta, prefix = '') {
  const out = [];
  if (!delta || typeof delta !== 'object') return out;

  // Array container
  if (delta._t === 'a') {
    Object.keys(delta).forEach((key) => {
      if (key === '_t') return;
      const child = delta[key];
      const isRemoval = key.startsWith('_');
      const idx = isRemoval ? key.slice(1) : key;
      const itemPath = `${prefix}[${idx}]`;
      if (Array.isArray(child)) {
        if (child.length === 1) out.push({ kind: 'added', path: itemPath, newVal: child[0] });
        else if (child.length === 3 && child[1] === 0 && child[2] === 0) out.push({ kind: 'removed', path: itemPath, oldVal: child[0] });
        else if (child.length === 2) out.push({ kind: 'changed', path: itemPath, oldVal: child[0], newVal: child[1] });
        else out.push({ kind: 'changed', path: itemPath, oldVal: child[0], newVal: child[1] });
      } else if (child && typeof child === 'object') {
        out.push(...flattenDelta(child, itemPath));
      }
    });
    return out;
  }

  // Plain object
  Object.keys(delta).forEach((key) => {
    const child = delta[key];
    const childPath = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(child)) {
      if (child.length === 1) out.push({ kind: 'added', path: childPath, newVal: child[0] });
      else if (child.length === 3 && child[1] === 0 && child[2] === 0) out.push({ kind: 'removed', path: childPath, oldVal: child[0] });
      else if (child.length === 2) out.push({ kind: 'changed', path: childPath, oldVal: child[0], newVal: child[1] });
      else out.push({ kind: 'changed', path: childPath, oldVal: child[0], newVal: child[1] });
    } else if (child && typeof child === 'object') {
      out.push(...flattenDelta(child, childPath));
    }
  });
  return out;
}

export default function AuditDiffView({ delta }) {
  if (!delta || (typeof delta === 'object' && Object.keys(delta).length === 0)) {
    return (
      <div className="text-[11.5px] italic text-[#7a7060] px-3 py-2 rounded-[8px] border" style={{ borderColor: '#e8e2d6', background: '#faf8f4' }}>
        No field-level changes recorded.
      </div>
    );
  }

  const changes = flattenDelta(delta);
  if (changes.length === 0) {
    return (
      <div className="text-[11.5px] italic text-[#7a7060] px-3 py-2 rounded-[8px] border" style={{ borderColor: '#e8e2d6', background: '#faf8f4' }}>
        Diff present but no leaf-level changes parsed.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {changes.map((c, i) => (
        <ChangeRow
          key={i}
          pathKey={c.path}
          oldVal={c.oldVal}
          newVal={c.newVal}
          kind={c.kind}
        />
      ))}
    </div>
  );
}
